import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { PlayCircle, Lock, UserPlus, Loader2, Maximize2, Minimize2 } from 'lucide-react';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import VisibleBreadcrumbs from '@/components/VisibleBreadcrumbs';
import { API } from '@/config';
import { ContentGateModal, gateFor } from '@/components/ContentGate';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Block keyboard shortcuts that enable saving / inspecting / dev tools.
// This is best-effort UI deterrent only — a determined user can always grab
// the underlying stream. See ProtectedVideo for the heavier hardening.
const blockShortcuts = (e) => {
  if (e.ctrlKey && ['s', 'u', 'p', 'S', 'U', 'P'].includes(e.key)) {
    e.preventDefault(); e.stopPropagation();
  }
  if (e.key === 'F12') { e.preventDefault(); e.stopPropagation(); }
  if (e.ctrlKey && e.shiftKey && ['I', 'J', 'C', 'i', 'j', 'c'].includes(e.key)) {
    e.preventDefault(); e.stopPropagation();
  }
};

// YouTube URL → embed URL converter. Handles youtu.be, watch?v=, /embed/, /shorts/.
// hl=sv forces YouTube's own player UI ("Watch on", caption labels, …) to be
// rendered in Swedish — otherwise YouTube auto-picks the user's browser
// locale and would, for example, label the video in Arabic for AR users.
const getYouTubeEmbed = (url) => {
  if (!url) return null;
  const base = (id) => `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&hl=sv&cc_lang_pref=sv&fs=0`;
  // youtu.be/<id>
  const m1 = url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
  if (m1) return base(m1[1]);
  // youtube.com/watch?v=<id>
  const m2 = url.match(/youtube\.com\/watch\?v=([A-Za-z0-9_-]{6,})/);
  if (m2) return base(m2[1]);
  // youtube.com/embed/<id> | /shorts/<id>
  const m3 = url.match(/youtube\.com\/(?:embed|shorts)\/([A-Za-z0-9_-]{6,})/);
  if (m3) return base(m3[1]);
  return null;
};

// Extract YouTube video ID so we can auto-derive a thumbnail when the admin
// didn't upload one. Works for youtu.be, watch?v=, /embed/, /shorts/.
const getYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /youtu\.be\/([A-Za-z0-9_-]{6,})/,
    /youtube\.com\/watch\?v=([A-Za-z0-9_-]{6,})/,
    /youtube\.com\/(?:embed|shorts)\/([A-Za-z0-9_-]{6,})/
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

// Returns a thumbnail URL — prefers the admin-uploaded image, falls back
// to YouTube's auto-generated hqdefault for YouTube videos.
const getThumbnail = (video) => {
  if (video.thumbnail_url && video.thumbnail_url.trim()) return video.thumbnail_url;
  const ytId = getYouTubeId(video.video_url);
  if (ytId) return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
  return null;
};

// Hardened video player: disables download UI, right-click, picture-in-picture,
// drag-to-save, and overlays a transparent guard on top so users can't easily
// reach the underlying <video> element with right-click. The video URL itself
// is still discoverable in DevTools but most casual users will not bother.
const ProtectedVideo = ({ src, poster, title }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const youtubeEmbed = getYouTubeEmbed(src);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const blockMenu = (e) => { e.preventDefault(); return false; };
    v.addEventListener('contextmenu', blockMenu);
    return () => v.removeEventListener('contextmenu', blockMenu);
  }, []);

  // Track fullscreen state across all browser-specific events so the toggle
  // button always shows the right icon and can exit fullscreen reliably.
  useEffect(() => {
    const onChange = () => {
      const fsEl = document.fullscreenElement
        || document.webkitFullscreenElement
        || document.msFullscreenElement;
      setIsFullscreen(!!fsEl);
    };
    const events = ['fullscreenchange', 'webkitfullscreenchange', 'msfullscreenchange'];
    events.forEach((e) => document.addEventListener(e, onChange));
    return () => events.forEach((e) => document.removeEventListener(e, onChange));
  }, []);

  // Toggle fullscreen mode: enter on click, exit on second click. Falls back
  // to webkit prefixes for older iOS Safari which exposes the API only on
  // the <video> element itself.
  const toggleFullscreen = () => {
    const fsEl = document.fullscreenElement
      || document.webkitFullscreenElement
      || document.msFullscreenElement;

    if (fsEl) {
      // Exit fullscreen
      const exit = document.exitFullscreen
        || document.webkitExitFullscreen
        || document.msExitFullscreen;
      if (exit) exit.call(document).catch(() => {});
      return;
    }

    // Enter fullscreen on the container so iframe / video / watermark
    // all expand together.
    const el = containerRef.current;
    if (!el) return;
    const enter = el.requestFullscreen
      || el.webkitRequestFullscreen
      || el.msRequestFullscreen;
    if (enter) {
      enter.call(el).catch(() => {});
    } else if (videoRef.current?.webkitEnterFullscreen) {
      videoRef.current.webkitEnterFullscreen();
    }
  };

  // YouTube videos require an iframe — protection via player params + overlay.
  if (youtubeEmbed) {
    return (
      <div
        ref={containerRef}
        className="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-lg select-none group"
        onContextMenu={(e) => e.preventDefault()}
      >
        <iframe
          src={youtubeEmbed}
          title={title}
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          className="w-full h-full pointer-events-auto"
          frameBorder="0"
          data-testid="protected-video-player"
        />
        <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white text-xs px-2 py-1 rounded pointer-events-none">
          drivemaster.se
        </div>
        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          data-testid="video-fullscreen-btn"
          className="absolute bottom-3 right-3 z-20 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-1.5 text-xs font-semibold transition-all"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          <span className="hidden sm:inline">
            {isFullscreen ? 'Stäng' : 'Fullskärm'}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-lg group select-none"
      onContextMenu={(e) => e.preventDefault()}
      style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        controls
        controlsList="nodownload noremoteplayback noplaybackrate"
        disablePictureInPicture
        disableRemotePlayback
        playsInline
        preload="metadata"
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        className="w-full h-full object-contain pointer-events-auto"
        data-testid="protected-video-player"
      >
        <track kind="captions" />
      </video>
      {/* Watermark overlay */}
      <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white text-xs px-2 py-1 rounded pointer-events-none">
        drivemaster.se
      </div>
      <button
        type="button"
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        data-testid="video-fullscreen-btn"
        className="absolute top-3 left-3 z-20 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-1.5 text-xs font-semibold transition-all"
      >
        {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        <span className="hidden sm:inline">
          {isFullscreen ? 'Stäng' : 'Fullskärm'}
        </span>
      </button>
      <span className="sr-only">{title}</span>
    </div>
  );
};

const VideosPage = () => {
  const { i18n, t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [gateMode, setGateMode] = useState(null);

  useEffect(() => {
    // Global key blocker while user is on this page.
    document.addEventListener('keydown', blockShortcuts);
    return () => document.removeEventListener('keydown', blockShortcuts);
  }, []);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await axios.get(`${API}/videos`);
        setVideos(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to fetch videos', e);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  const canWatch = isAuthenticated && (
    user?.subscription_status === 'active' ||
    user?.taxi_subscription_status === 'active' ||
    user?.has_active_subscription ||
    user?.is_admin ||
    user?.role === 'admin'
  );
  const lang = i18n.language || 'sv';
  // Number of videos visible to non-subscribers as a "preview" to drive
  // conversions. First two are unlocked for everyone; rest require a plan.

  // Sort by `order` ascending so admin can control which are the free ones.
  const sortedVideos = [...videos].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const titles = {
    sv: 'Teorivideor - Lär dig genom video',
    ar: 'فيديوهات تعليمية - تعلم بالفيديو',
    en: 'Theory videos - Learn through video',
    tr: 'Teori videoları',
    fa: 'ویدئوهای تئوری',
    so: 'Fiidiyowyada Aragtida',
    pl: 'Filmy teoretyczne',
    es: 'Videos teóricos',
    de: 'Theorie-Videos'
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-purple-50/30 to-pink-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900">
      <Helmet>
        <title>{titles[lang] || titles.sv} | DriveMaster</title>
        <meta name="description" content="Gratis körkortsteori videor på 9 språk. Lär dig trafikregler, vägmärken och säker körning genom video." />
      </Helmet>
      <Navbar />
      <VisibleBreadcrumbs />

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <header className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2" data-testid="videos-page-title">
            {titles[lang] || titles.sv}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-3">
            {lang === 'ar' ? 'تعلم القيادة الآمنة من خلال فيديوهات تعليمية مختصرة' : 'Lär dig säker körning genom korta videolektioner'}
          </p>
          {/* Swedish-only notice */}
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-1.5 text-xs sm:text-sm text-amber-800 dark:text-amber-300" data-testid="swedish-only-notice">
            <span className="text-base">🇸🇪</span>
            <span>
              {{
                sv: 'Videor finns endast på svenska',
                ar: 'الفيديوهات متوفرة بالسويدية فقط',
                en: 'Videos are only available in Swedish',
                tr: 'Videolar sadece İsveççe mevcuttur',
                fa: 'ویدئوها فقط به سوئدی موجود است',
                so: 'Fiidiyowyada waxa kaliya ee laga heli karo waa Iswiidan',
                pl: 'Filmy dostępne tylko po szwedzku',
                es: 'Los videos están solo en sueco',
                de: 'Videos sind nur auf Schwedisch verfügbar'
              }[lang] || 'Videor finns endast på svenska'}
            </span>
          </div>
        </header>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : sortedVideos.length === 0 ? (
          /* "Coming soon" state — shown automatically while the videos
              collection is empty. Disappears as soon as admin adds a video. */
          <div className="py-16 sm:py-24" data-testid="videos-coming-soon">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/40 dark:to-pink-900/40 flex items-center justify-center">
                <PlayCircle className="w-10 h-10 sm:w-12 sm:h-12 text-purple-500 animate-pulse" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 mb-3">
                {{
                  sv: 'Videor kommer snart',
                  ar: 'الفيديوهات قادمة قريباً',
                  en: 'Videos coming soon',
                  tr: 'Videolar yakında geliyor',
                  fa: 'ویدئوها به‌زودی می‌آیند',
                  so: 'Fiidiyowyada way iman doonaan dhowaan',
                  pl: 'Filmy wkrótce',
                  es: 'Videos próximamente',
                  de: 'Videos kommen bald'
                }[lang] || 'Videor kommer snart'}
              </h2>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                {{
                  sv: 'Vi förbereder noggrant utvalda videolektioner som hjälper dig att klara teoriprovet. Kom tillbaka snart!',
                  ar: 'نقوم بإعداد فيديوهات تعليمية مختارة بعناية لمساعدتك على اجتياز اختبار النظري. عد قريباً!',
                  en: 'We are preparing carefully curated video lessons to help you pass the theory test. Check back soon!',
                  tr: 'Teori sınavını geçmenize yardımcı olacak özenle seçilmiş video dersleri hazırlıyoruz. Yakında tekrar gelin!',
                  fa: 'در حال آماده‌سازی ویدئوهای آموزشی منتخب برای کمک به شما در آزمون تئوری هستیم. به‌زودی برگردید!',
                  so: 'Waxaan diyaarinaynaa cashar-fiidiyow si taxadar leh loo doortay si aad u gudubto imtixaanka aragtida. Soo noqo dhowaan!',
                  pl: 'Przygotowujemy starannie wyselekcjonowane lekcje wideo, które pomogą Ci zdać egzamin teoretyczny. Wróć wkrótce!',
                  es: 'Estamos preparando lecciones en video cuidadosamente seleccionadas para ayudarte a aprobar el examen teórico. ¡Vuelve pronto!',
                  de: 'Wir bereiten sorgfältig ausgewählte Videolektionen vor, die dir helfen, die Theorieprüfung zu bestehen. Schau bald wieder vorbei!'
                }[lang] || 'Vi förbereder noggrant utvalda videolektioner. Kom tillbaka snart!'}
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-xs sm:text-sm text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
                {{
                  sv: 'Under utveckling',
                  ar: 'قيد التطوير',
                  en: 'In development',
                  tr: 'Geliştirme aşamasında',
                  fa: 'در حال توسعه',
                  so: 'Horumarinta',
                  pl: 'W przygotowaniu',
                  es: 'En desarrollo',
                  de: 'In Entwicklung'
                }[lang] || 'Under utveckling'}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {sortedVideos.map((v, idx) => {
              // First N videos are free for everyone (no subscription required).
              const isFree = idx < 2;
              const gate = gateFor(idx, user, canWatch);
              const isLocked = gate !== 'open';
              const thumb = getThumbnail(v);
              return (
                <button
                  key={v.id}
                  onClick={() => {
                    if (isLocked) {
                      setGateMode(gate);
                      return;
                    }
                    setSelectedVideo(v);
                  }}
                  className="group relative bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all text-left"
                  data-testid={`video-card-${v.id}`}
                >
                  <div className="relative aspect-video bg-slate-100 dark:bg-slate-700 overflow-hidden">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={v.title?.sv || v.title?.en || 'Video'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <PlayCircle className="w-12 h-12 text-slate-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                      {isLocked ? (
                        gate === 'register'
                          ? <UserPlus className="w-8 h-8 sm:w-10 sm:h-10 text-white drop-shadow-lg" />
                          : <Lock className="w-8 h-8 sm:w-10 sm:h-10 text-white drop-shadow-lg" />
                      ) : (
                        <PlayCircle className="w-10 h-10 sm:w-14 sm:h-14 text-white drop-shadow-lg" />
                      )}
                    </div>
                    {/* "FREE" badge on the first N videos */}
                    {isFree && (
                      <span className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded">
                        {lang === 'ar' ? 'مجاني' : 'GRATIS'}
                      </span>
                    )}
                    {v.duration > 0 && (
                      <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] sm:text-xs px-1.5 py-0.5 rounded">
                        {Math.floor(v.duration / 60)}:{String(v.duration % 60).padStart(2, '0')}
                      </span>
                    )}
                  </div>
                  <div className="p-2.5 sm:p-4">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 line-clamp-2 mb-1">
                      {v.title?.sv || v.title?.en || 'Untitled'}
                    </h3>
                    <p className="hidden sm:line-clamp-2 sm:block text-xs text-slate-500 dark:text-slate-400">
                      {v.description?.sv || ''}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Old empty-state removed — replaced by the "coming soon" block above. */}
      </main>

      {/* Video player modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
          data-testid="video-player-modal"
        >
          <div
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-10 right-0 text-white hover:text-slate-300 text-sm"
              data-testid="close-video-btn"
            >
              {lang === 'ar' ? '✕ إغلاق' : '✕ Stäng'}
            </button>
            <ProtectedVideo
              src={selectedVideo.video_url}
              poster={getThumbnail(selectedVideo)}
              title={selectedVideo.title?.sv || ''}
            />
            <h2 className="mt-4 text-white text-lg font-semibold">
              {selectedVideo.title?.sv}
            </h2>
            <p className="mt-1 text-slate-300 text-sm">
              {selectedVideo.description?.sv}
            </p>
          </div>
        </div>
      )}

      <ContentGateModal mode={gateMode} onClose={() => setGateMode(null)} lang={lang} />
      <Footer />
    </div>
  );
};

export default VideosPage;
